import React, { ChangeEvent, FC, Fragment, useEffect, useState } from 'react';
import {
    Box,
    Alert,
    Grid,
    FormControl,
    TextField,
    InputLabel,
    Select,
    MenuItem,
    SelectChangeEvent,
    IconButton,
    Button,
    useMediaQuery,
    Theme,
    Tooltip,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import { ActionBasedActionListFilters, LabClient } from 'models/client-directory';
import { ActionPrice } from 'models/organization';
import { useQuery } from '@apollo/client';
import { getActionBasedActionListTemplate, getMaterialGroupsTemplate } from 'apollo/templates/client-directory';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ActionBasedClinicActionList from './action-based-clinic-action-list/action-based-clinic-action-list.component';
import Loader from 'components/shared/loader/loader.component';
import { customerSettings } from 'utils/customerSettings';
import { CONSTANTS } from 'config/constants';
import { OrderBy } from 'models/search-result';
import { DEFAULT_ACTION_BASED_ORDER, sortActionBasedActions, toggleOrderBy } from 'utils/price-list-sort';

type ConnectedClinicActionProps = {
    labClient: LabClient;
    clientCurrency: string;
};
const ConnectedClinicAction: FC<ConnectedClinicActionProps> = ({
    labClient,
    clientCurrency,
}: ConnectedClinicActionProps) => {
    const { t: translate } = useTranslation(['common']);
    const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('lg'));
    const { id } = useParams<{ id: string }>();
    const [filters, setFilters] = useState<ActionBasedActionListFilters>({ freetext: '', mainCategory: '' });
    const [category, setCategory] = useState('');
    const [uniqueCategories, setUniqueCategories] = useState<string[]>([]);
    const [actionBasedActionList, setActionBasedActionList] = useState<ActionPrice[]>([]);
    const [orderBy, setOrderBy] = useState<OrderBy>(DEFAULT_ACTION_BASED_ORDER);
    const [showAllPriceSupplement, setShowAllPriceSupplement] = useState(false);
    const [displayShowAllButton, setDisplayShowAllButton] = useState<boolean>(false);
    const { showBoundary } = useDenthubErrorBoundary();
    const canEditActionList = customerSettings.getCustomerSetting(
        labClient.customer?.customerSettings || [],
        CONSTANTS.SETTINGS_NAMES.EDIT_ACTION_PRICE_LIST,
    );

    const { loading: actionBasedActionListLoading, refetch: refetchActionList } = useQuery(
        getActionBasedActionListTemplate,
        {
            notifyOnNetworkStatusChange: true,
            variables: {
                clinicId: id,
                filters: filters,
            },
            onError(error) {
                showBoundary(error);
            },
            onCompleted({ actionBasedActionList }) {
                setActionBasedActionList(actionBasedActionList || []);
            },
        },
    );

    const {
        data: { materialGroups } = {},
        loading: materialGroupsLoading,
        refetch: refetchMaterialGroups,
    } = useQuery(getMaterialGroupsTemplate, {
        notifyOnNetworkStatusChange: true,
        variables: {
            clinicId: id,
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const handleFreetextFilter = (event: ChangeEvent<HTMLInputElement>) => {
        setFilters({ ...filters, freetext: event.target.value.toLocaleLowerCase() });
    };

    const handleCategoryFilterChange = (event: SelectChangeEvent<string>) => {
        const category = uniqueCategories.find((category) => category === event.target.value);
        if (category) {
            setCategory(category);
            setFilters({ ...filters, mainCategory: category });
        }
    };

    const handleClearCategory = () => {
        setCategory('');
        setFilters({ ...filters, mainCategory: '' });
    };

    const handleShowAllPriceSupplementChange = () => {
        setShowAllPriceSupplement((prev) => !prev);
    };

    const handleSortChange = (field: string) => {
        setOrderBy(toggleOrderBy(orderBy, field));
    };

    const handleDisplayShowAllButton = (arg: boolean) => {
        setDisplayShowAllButton(arg);
    };

    useEffect(() => {
        if (!actionBasedActionListLoading) {
            const categories = actionBasedActionList.map((action: ActionPrice) => action.mainCategory);
            setUniqueCategories(
                categories.filter((c: string, index: number) => {
                    return categories.indexOf(c) === index;
                }),
            );
        }
    }, [actionBasedActionList]);

    return (
        <Fragment>
            {canEditActionList ? undefined : (
                <Box mb={1}>
                    <Alert severity="info">{translate('common:ACTION_BASED_CLINIC_INFO_TEXT')}</Alert>
                </Box>
            )}

            <Box display="flex" alignItems="center" flexDirection={!isMobile ? 'row' : 'column-reverse'}>
                <Grid container spacing={1}>
                    <Grid item md={3} xs={12}>
                        <FormControl margin="dense" fullWidth>
                            <TextField
                                id="freetext-filter"
                                label={translate('client-directory:FREETEXT_CODE_OR_ACTION')}
                                onChange={handleFreetextFilter}
                            />
                        </FormControl>
                    </Grid>
                    <Grid item md={3} xs={12}>
                        <FormControl margin="dense" fullWidth>
                            <InputLabel>{translate('client-directory:MAIN_CATEGORY')}</InputLabel>
                            <Select
                                value={category}
                                label={translate('client-directory:MAIN_CATEGORY')}
                                onChange={handleCategoryFilterChange}
                                endAdornment={
                                    <Fragment>
                                        {category !== '' && (
                                            <IconButton
                                                size="small"
                                                sx={{ marginRight: '.75rem' }}
                                                onClick={handleClearCategory}
                                            >
                                                <ClearIcon />
                                            </IconButton>
                                        )}
                                    </Fragment>
                                }
                            >
                                {uniqueCategories.map((category: string, index: number) => (
                                    <MenuItem key={index} value={category}>
                                        {category}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>
                {!isMobile && displayShowAllButton && (
                    <Box flex="1 0 auto">
                        <Tooltip
                            title={
                                showAllPriceSupplement
                                    ? translate('client-directory:HIDE_ALL_PRICE_SUPPLEMENT').toString()
                                    : translate('client-directory:SHOW_ALL_PRICE_SUPPLEMENT').toString()
                            }
                        >
                            <Button
                                variant={showAllPriceSupplement ? 'outlined' : 'contained'}
                                onClick={handleShowAllPriceSupplementChange}
                                sx={{ minWidth: 125 }}
                            >
                                {showAllPriceSupplement
                                    ? translate('common:HIDE_ALL').toString()
                                    : translate('common:SHOW_ALL').toString()}
                            </Button>
                        </Tooltip>
                    </Box>
                )}
            </Box>
            {actionBasedActionListLoading ? (
                <Loader />
            ) : (
                <ActionBasedClinicActionList
                    clientCurrency={clientCurrency}
                    clientCustomerSettings={labClient.customer?.customerSettings || []}
                    clientId={id || ''}
                    // DD-3697: the price history belongs to the chain (the parent of a clinic tree).
                    chainId={labClient.customer?.parentClinicId || id || ''}
                    actions={sortActionBasedActions(actionBasedActionList, orderBy)}
                    orderBy={orderBy}
                    onSortChange={handleSortChange}
                    showAllPriceSupplement={showAllPriceSupplement}
                    materialGroups={materialGroups}
                    materialGroupsLoading={materialGroupsLoading}
                    onDisplayShowAllButton={handleDisplayShowAllButton}
                    refetchActionList={refetchActionList}
                    refetchMaterialGroups={refetchMaterialGroups}
                />
            )}
        </Fragment>
    );
};

export default ConnectedClinicAction;
